import { named, optional, withDependencies } from '@wix/thunderbolt-ioc'
import { isSSR, getHeaderElement } from '@wix/thunderbolt-commons'
import type {
	IResolvableReadyForScrollPromise,
	IWindowScrollAPI,
	WindowScrollMasterPageConfig,
	WindowScrollPageConfig,
} from './types'
import { ScrollAnimationResult, ScrollToCallbacks } from './types'
import { calcScrollDuration } from './scrollUtils'
import {
	BrowserWindowSymbol,
	Experiments,
	ExperimentsSymbol,
	IStructureAPI,
	PageFeatureConfigSymbol,
	Structure,
	ViewMode,
	ViewModeSym,
	ReducedMotionSymbol,
	MasterPageFeatureConfigSymbol,
} from '@wix/thunderbolt-symbols'
import { ILightboxUtils, LightboxUtilsSymbol } from 'feature-lightbox'
import { name, ResolvableReadyForScrollPromiseSymbol } from './symbols'
import { ISiteScrollBlocker, SiteScrollBlockerSymbol } from 'feature-site-scroll-blocker'
import { isElementTabbable } from 'feature-cyclic-tabbing'
import { sineInOut } from '@wix/wow-utils/easing'
import { AnimatedScrollManager } from './AnimatedScrollManager'

// Returns the current HTMLElement matching the selector, or null if not found.
// This function is essential in the window scroll flow because async operations (such as awaits) can cause DOM re-renders or updates,
// making previously-retrieved element references stale or detached. Always use this function to re-query the DOM for the latest element
// before performing operations, especially after awaits. This helps prevent flakiness due to stale references.
const getTargetElement = (selector: string): HTMLElement | null =>
	window.document.querySelector(selector) as HTMLElement | null

const getFocusableElement = (selector: string, isExperimentOpen: boolean): HTMLElement | null => {
	if (isExperimentOpen) {
		if (selector === '#SCROLL_TO_TOP') {
			return window.document.querySelector('main section') as HTMLElement | null
		} else {
			return window.document.querySelector(selector) as HTMLElement | null
		}
	}
	return window.document.querySelector(selector) as HTMLElement | null
}

const getPosition = (elem: HTMLElement) => window.getComputedStyle(elem).getPropertyValue('position').toLowerCase()

const getStickyParentId = (compId: string): string | undefined => {
	let element = window.document.getElementById(compId)
	let stickyElement
	while (element) {
		const computedStyle = window.getComputedStyle(element)
		if (computedStyle.position === 'sticky') {
			stickyElement = element
		}
		element = element.parentElement as HTMLElement
	}

	return stickyElement?.id
}

const isElementOrAncestorFixed = (element: HTMLElement) => {
	let elem = element
	while (elem && elem !== window.document.body) {
		if (getPosition(elem) === 'fixed') {
			return true
		}
		elem = elem.offsetParent as HTMLElement
	}
	return false
}

const pxToNumber = (pxSize: string) => Number(pxSize.replace('px', ''))

const getScrollableElement = (popupUtils?: ILightboxUtils) => {
	return popupUtils?.getCurrentLightboxId() ? window.document.getElementById('POPUPS_ROOT')! : window
}

const isStickyElement = (element: HTMLElement) => {
	return getPosition(element) === 'sticky'
}
const getWixAdsHeight = () => {
	const wixAdsElement = window.document.getElementById('WIX_ADS')
	return wixAdsElement ? wixAdsElement.offsetHeight : 0
}

function getTopLocation(element: HTMLElement | Window): number {
	if (element instanceof HTMLElement) {
		return element.scrollTop
	} else {
		return element.scrollY
	}
}

export const WindowScroll = withDependencies(
	[
		BrowserWindowSymbol,
		ViewModeSym,
		ResolvableReadyForScrollPromiseSymbol,
		SiteScrollBlockerSymbol,
		ExperimentsSymbol,
		Structure,
		ReducedMotionSymbol,
		named(PageFeatureConfigSymbol, name),
		named(MasterPageFeatureConfigSymbol, name),
		optional(LightboxUtilsSymbol),
	],
	(
		window: Window,
		viewMode: ViewMode,
		{ readyForScrollPromise }: IResolvableReadyForScrollPromise,
		siteScrollBlockerApi: ISiteScrollBlocker,
		experiments: Experiments,
		structureApi: IStructureAPI,
		reducedMotion,
		{ headerContainerComponentId }: WindowScrollPageConfig,
		{ isHeaderAnimated }: WindowScrollMasterPageConfig,
		popupUtils?: ILightboxUtils
	): IWindowScrollAPI => {
		let animationFrameId: number | null = null

		if (isSSR(window)) {
			return {
				scrollToComponent: () => Promise.resolve(),
				animatedScrollTo: () => Promise.resolve(ScrollAnimationResult.Aborted),
				scrollToSelector: () => Promise.resolve(),
			}
		}

		const getHeaderOffset = (distanceBetweenCompTopToBodyTop: number, wixAdsHeight: number) => {
			const headerElement = getHeaderElement(headerContainerComponentId, window, experiments)
			if (!headerElement) {
				return 0
			}

			const headerPosition = getPosition(headerElement)

			const isHeaderStickyOrFixed = headerPosition === 'fixed' || headerPosition === 'sticky'

			const headerHeight = headerElement.getBoundingClientRect().height
			// This is potential scroll amount
			const distanceBetweenHeaderBottomAndCompTop = Math.abs(
				distanceBetweenCompTopToBodyTop - headerHeight - wixAdsHeight
			)
			const isDistanceSmallerThanHeaderHeight = distanceBetweenHeaderBottomAndCompTop < headerHeight

			// animation is triggered when scroll position equals to header height, therefore as long as the distance between
			// header bottom and compTop is less than or equals to headerHeight, the header appears on the screen,
			// and it's height should be taken into account

			return isHeaderStickyOrFixed && (!isHeaderAnimated || isDistanceSmallerThanHeaderHeight) ? headerHeight : 0
		}

		const getAnimationFrameClientYForScroll = (compId: string, openLightboxId: string | undefined) => {
			return new Promise<number>((resolve) => {
				window.requestAnimationFrame(() => {
					const compNode = getTargetElement(compId)
					if (!compNode) {
						resolve(0)
						return
					}
					resolve(getCompClientYForScroll(compNode, openLightboxId))
				})
			})
		}
		const getCompClientYForScroll = (compNode: HTMLElement, openLightboxId: string | undefined) => {
			const openLightboxElement = openLightboxId && window.document.getElementById(openLightboxId)
			let bodyTop = openLightboxElement
				? openLightboxElement.getBoundingClientRect().top
				: window.document.body.getBoundingClientRect().top

			const isScrollBlocked = siteScrollBlockerApi.isScrollingBlocked()
			if (isScrollBlocked) {
				const siteContainerElement = window.document.getElementById('SITE_CONTAINER')
				bodyTop = siteContainerElement ? pxToNumber(window.getComputedStyle(siteContainerElement).marginTop) : 0
			}

			const wixAdsHeight = getWixAdsHeight()

			const compTop = compNode.getBoundingClientRect().top
			const distanceBetweenCompTopToBodyTop = compTop - bodyTop
			const headerOffset = getHeaderOffset(distanceBetweenCompTopToBodyTop, wixAdsHeight)

			return distanceBetweenCompTopToBodyTop - wixAdsHeight - (openLightboxId ? 0 : headerOffset)
		}

		// Create the AnimatedScrollManager instance
		const scrollManager = experiments['specs.thunderbolt.updateScrollTarget']
			? new AnimatedScrollManager(
					window,
					viewMode,
					reducedMotion,
					() => getScrollableElement(popupUtils),
					getTopLocation,
					addScrollInteractionEventListeners,
					getCompClientYForScroll,
					removeScrollInteractionEventListeners
			  )
			: null

		const animatedScrollTo = async (
			targetY: number,
			callbacks: ScrollToCallbacks = {}
		): Promise<ScrollAnimationResult> => {
			await readyForScrollPromise
			const scrollableElement = getScrollableElement(popupUtils)

			if (reducedMotion) {
				scrollableElement.scrollTo({ top: targetY })
				callbacks.onComplete?.()
				return ScrollAnimationResult.Completed
			}
			if (animationFrameId !== null) {
				cancelAnimationFrame(animationFrameId)
				animationFrameId = null
			}

			return new Promise((resolve) => {
				function scrollAbortHandler() {
					removeScrollInteractionEventListeners(scrollAbortHandler)
					scrollableElement.scrollTo({ top: getTopLocation(scrollableElement) })
					cancelAnimationFrame(animationFrameId!)
					animationFrameId = null
					resolve(ScrollAnimationResult.Aborted)
				}

				addScrollInteractionEventListeners(scrollAbortHandler)
				const startY = getTopLocation(scrollableElement)
				const distance = targetY - startY
				const isMobile = viewMode === 'mobile'
				const duration = calcScrollDuration(window.pageYOffset, targetY, isMobile) * 1000
				const startTime = performance.now()

				function scrollStep(timestamp: number) {
					const elapsed = timestamp - startTime
					const progress = Math.min(elapsed / duration, 1)
					const newPosition = startY + distance * sineInOut(progress)
					scrollableElement.scrollTo(0, newPosition)

					if (elapsed < duration) {
						animationFrameId = window.requestAnimationFrame(scrollStep)
					} else {
						scrollableElement.scrollTo(0, targetY)
						animationFrameId = null
						removeScrollInteractionEventListeners(scrollAbortHandler)
						callbacks.onComplete?.()
						resolve(ScrollAnimationResult.Completed)
					}
				}

				window.requestAnimationFrame(scrollStep)
			})
		}

		const animatedScrollToWithUpdateScrollTarget = async (
			targetY: number,
			callbacks: ScrollToCallbacks = {},
			targetSetterCallback?: (setTargetYCallback: (newTargetY: number) => void) => void
		): Promise<ScrollAnimationResult> => {
			return scrollManager!.scrollTo(targetY, callbacks, targetSetterCallback)
		}

		const newAnimatedScrollTo = scrollManager ? animatedScrollToWithUpdateScrollTarget : animatedScrollTo

		const scrollToSelector = async (
			selector: string,
			openLightboxId?: string,
			{ callbacks = {}, skipScrollAnimation = false } = {},
			numOfRetries: number = 5
		) => {
			await readyForScrollPromise
			const readyForScrollTargetElement = getTargetElement(selector)
			if (
				!readyForScrollTargetElement ||
				(isElementOrAncestorFixed(readyForScrollTargetElement) && !openLightboxId)
			) {
				return
			}
			const compClientYForScroll = await getAnimationFrameClientYForScroll(selector, openLightboxId)

			if (skipScrollAnimation) {
				window.scrollTo({ top: 0 })
			} else {
				let targetSetter: (newY: number) => void
				let stopObserving: (() => void) | undefined
				const observedElement = getTargetElement(selector)
				if (!observedElement) {
					return
				}
				if (scrollManager) {
					stopObserving = scrollManager.observeElementYPosition(
						observedElement,
						openLightboxId!,
						compClientYForScroll,
						(newY: number, prevY: number) => {
							if (newY !== prevY) {
								if (targetSetter) {
									targetSetter(newY)
								}
							}
						}
					)
				}

				const result = await newAnimatedScrollTo(
					compClientYForScroll,
					callbacks,
					async (newTargetSetterFunc) => {
						targetSetter = newTargetSetterFunc
					}
				)

				if (result !== ScrollAnimationResult.Aborted) {
					let compClientYForScrollAfterScroll: number
					if (experiments['specs.thunderbolt.windowScrollRequestAnimationFrame']) {
						compClientYForScrollAfterScroll = await getAnimationFrameClientYForScroll(
							selector,
							openLightboxId
						)
					} else {
						const afterScrollElement = getTargetElement(selector)
						if (!afterScrollElement) {
							stopObserving?.()
							return
						}
						compClientYForScrollAfterScroll = getCompClientYForScroll(afterScrollElement, openLightboxId)
					}

					const afterScrollElement = getTargetElement(selector)
					if (!afterScrollElement) {
						stopObserving?.()
						return
					}
					const retryThreshold = 0.5
					const compYDelta = Math.abs(compClientYForScroll - compClientYForScrollAfterScroll)
					const shouldRetryScroll =
						!isStickyElement(afterScrollElement) && compYDelta > retryThreshold && numOfRetries > 0

					if (shouldRetryScroll) {
						// if the anchor original position changed due to dynamic
						// content above it height change pushing anchor down
						// we need to perform scroll logic again until reaching the anchor
						void scrollToSelector(
							selector,
							openLightboxId,
							{
								callbacks,
								skipScrollAnimation,
							},
							compYDelta > 100 ? numOfRetries : numOfRetries - 1
						)
					}
				}

				stopObserving?.()
			}
			const isExperimentOpen: boolean = !!experiments['specs.thunderbolt.postTransitionElementFocus']
			const setAttributeTargetElement = getFocusableElement(selector, isExperimentOpen)
			if (!setAttributeTargetElement) {
				return
			}

			if (!isElementTabbable(setAttributeTargetElement)) {
				setAttributeTargetElement.setAttribute('tabIndex', '-1')
				setAttributeTargetElement.setAttribute('aria-label', 'main content')
			}
			setAttributeTargetElement.focus({ preventScroll: true })
		}

		const scrollToComponent = async (
			targetCompId: string,
			{ callbacks = {}, skipScrollAnimation = false } = {}
		) => {
			const targetCompData = structureApi.get(targetCompId)
			const openLightboxId = popupUtils?.getCurrentLightboxId()
			const isCompOnLightbox = targetCompData?.pageId === openLightboxId
			const stickyParentId = getStickyParentId(targetCompId)

			await scrollToSelector(
				`#${stickyParentId ?? targetCompId}`,
				isCompOnLightbox ? openLightboxId : undefined,
				{
					callbacks,
					skipScrollAnimation,
				}
			)

			if (experiments['specs.thunderbolt.scrollToAnchorSsr']) {
				document.documentElement.style.removeProperty('scroll-padding-top')
			}
		}

		function addScrollInteractionEventListeners(handler: () => void) {
			window.addEventListener('touchmove', handler, { passive: true })
			window.addEventListener('wheel', handler, { passive: true })
		}

		function removeScrollInteractionEventListeners(handler: () => void) {
			window.removeEventListener('touchmove', handler)
			window.removeEventListener('wheel', handler)
		}

		return {
			animatedScrollTo: newAnimatedScrollTo,
			scrollToComponent,
			scrollToSelector,
		}
	}
)
